import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading } from '../components/homepage';
import { caseStudies } from '../content/caseStudies';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPillSx,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const CaseStudiesPage: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/case-studies" />
      </Helmet>
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5.5, md: 7.5 }, pb: { xs: 6, md: 7 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3.5, lg: 5 }} alignItems="center">
            <Grid item xs={12} lg={5}>
              <SectionHeading
                eyebrow="Case studies"
                title="Two pilots, published in full. Real numbers, different stacks, different priority weightings."
                description="One pilot was conversion-weighted on a high-value resale stack. One was cost-and-risk-weighted across a multinational marketplace. Both moved the numbers the business tracks."
                marginBottom={2.5}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Request Demo
                </Button>
                <Button component={RouterLink} to="/resources" sx={marketingSecondaryButtonSx}>
                  Back to resources
                </Button>
              </Box>

              {/* Proof strip */}
              <Box
                sx={{
                  mt: 3.2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.4,
                  width: '100%',
                  maxWidth: 520,
                }}
              >
                {[
                  { value: '+15 pts', label: 'Checkout conversion', detail: 'Luxury goods resale pilot' },
                  { value: '−8%',     label: 'Blended payment cost', detail: 'Multinational marketplace' },
                  { value: '1.5% → <0.5%', label: 'Chargebacks in key markets', detail: 'Same pilot, cost + risk together' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      pl: 1.3,
                      borderLeft: '2px solid rgba(15,111,255,0.35)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: marketingDisplayFont,
                        fontSize: { xs: '1.05rem', md: '1.18rem' },
                        fontWeight: 400,
                        color: marketingPalette.accent,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                      {s.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Card sx={{ ...marketingCardSx, borderTop: '3px solid rgba(15,111,255,0.88)' }}>
                <Typography sx={{ color: marketingPalette.accent, fontSize: '0.74rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.8 }}>
                  What these cases prove
                </Typography>
                <Box sx={{ display: 'grid', gap: 0.9 }}>
                  {[
                    'Checkout improvement can happen without giving back risk control.',
                    'Payment cost and chargeback pressure can improve without narrowing checkout coverage.',
                    'The right proof ties local payment wins to healthier business outcomes.',
                  ].map((item) => (
                    <Typography key={item} sx={{ color: marketingPalette.headline, fontWeight: 400, lineHeight: 1.55 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <Grid container spacing={2.5} alignItems="stretch">
            {caseStudies.map((item) => (
              <Grid item xs={12} lg={6} key={item.slug}>
                <Card sx={{ ...marketingCardSx, height: '100%', display: 'grid', alignContent: 'start' }}>
                  <Box component="span" sx={marketingPillSx}>
                    {item.label}
                  </Box>
                  <Typography sx={{ mt: 1.1, color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.24, mb: 0.8 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.72, mb: 1.3 }}>
                    {item.summary}
                  </Typography>
                  <Grid container spacing={1}>
                    {item.highlights.map((highlight) => (
                      <Grid item xs={12} sm={4} key={highlight.label}>
                        <Box
                          sx={{
                            height: '100%',
                            px: 0.9,
                            py: 0.85,
                            border: '1px solid rgba(219,228,239,0.9)',
                            backgroundColor: '#fff',
                          }}
                        >
                          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.16 }}>
                            {highlight.label}
                          </Typography>
                          <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.88rem', lineHeight: 1.34 }}>
                            {highlight.value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 1.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.2 }}>
                    <Typography sx={{ color: marketingPalette.body, fontWeight: 400, lineHeight: 1.55 }}>
                      {item.cardDescription}
                    </Typography>
                    <Button component={RouterLink} to={`/case-studies/${item.slug}`} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                      Read
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <MarketingFinalCta
        kicker="Case studies · Next step"
        heading="Which of these numbers would matter most to your business?"
        body="Tell us which number would matter most — conversion, cost, or chargebacks. We'll open the call there. The optimization still balances all three."
        secondary={{ label: 'Read white papers', to: '/white-papers' }}
      />

      <Footer />
    </Box>
  );
};

export default CaseStudiesPage;
